import React from "react";

export default function AccountInspector({
  open,
  account,
  onClose,
  onGenerateLetter,
  onViewLetter,
  onDownloadPdf,
  generating = false,
}) {
  if (!open || !account) return null;

  const protectAccount =
    account.protect_account === true ||
    account.protect_account === 1 ||
    account.protect_account === "true";

  const noDisputeRecommended =
    protectAccount ||
    String(account.recommended_letter || "")
      .trim()
      .toLowerCase() === "no dispute recommended";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.45)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "520px",
          maxWidth: "100%",
          height: "100%",
          background: "#ffffff",
          boxShadow: "-8px 0 30px rgba(0,0,0,.18)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "28px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "900",
                color: "#0f172a",
              }}
            >
              🧠 AI Account Inspector
            </div>

            <div
              style={{
                marginTop: "6px",
                color: "#64748b",
                fontSize: "15px",
              }}
            >
              Intelligent account analysis
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "#e2e8f0",
              borderRadius: "12px",
              padding: "10px 16px",
              cursor: "pointer",
              fontWeight: "700",
            }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: "28px" }}>
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "18px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                fontSize: "17px",
                fontWeight: "900",
                color: "#1e3a8a",
                marginBottom: "10px",
              }}
            >
              🧠 Integrity AI Recommendation
            </div>

            <div
              style={{
                color: "#334155",
                lineHeight: 1.6,
                marginBottom: "14px",
              }}
            >
              {account.review_reason ||
                "Review this account for reporting accuracy, completeness, and consistency before taking action."}
            </div>

            <div
              style={{
                fontSize: "14px",
                fontWeight: "800",
                color: "#1e40af",
                marginBottom: "6px",
              }}
            >
              Recommended Action
            </div>

            <div
              style={{
                color: "#0f172a",
                fontWeight: "700",
                lineHeight: 1.5,
              }}
            >
              {account.next_action ||
                account.strategy ||
                "Review the account and select the appropriate dispute strategy."}
            </div>
          </div>

          <div
            style={{
              fontSize: "24px",
              fontWeight: "800",
              color: "#0f172a",
            }}
          >
            {account.creditor || "Unknown Creditor"}
          </div>

          <div
            style={{
              color: "#64748b",
              marginTop: "6px",
              marginBottom: "24px",
            }}
          >
            {account.account_type ||
              account.type ||
              "Unknown Account Type"}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <Metric
              title="Balance"
              value={account.balance || "$0"}
            />

            <Metric
              title="Status"
              value={account.status || "Unknown"}
            />

            <Metric
              title="AI Confidence"
              value={account.confidence_label || "--"}
            />

            <Metric
              title="Deletion Probability"
              value={account.deletion_probability || "--"}
            />
          </div>

          <Section
            title="AI Strategy"
            content={account.strategy}
          />

          <Section
            title="Recommended Letter"
            content={account.recommended_letter}
          />

          <Section
            title="Review Reason"
            content={account.review_reason}
          />

          <Section
            title="Flags"
            content={
              Array.isArray(account.flags)
                ? account.flags.join(", ")
                : account.flags
            }
          />

          <div
            style={{
              marginTop: "30px",
              display: "grid",
              gap: "14px",
            }}
          >
            <ActionButton
              text={
                noDisputeRecommended
                  ? "🛡️ No Dispute Recommended"
                  : generating
                  ? "Generating Letter..."
                  : "✉️ Generate Letter"
              }
              onClick={() => {
                if (!noDisputeRecommended) {
                  onGenerateLetter?.(account);
                }
              }}
              disabled={
                generating ||
                noDisputeRecommended
              }
              background={
                noDisputeRecommended
                  ? "#64748b"
                  : "#D4AF37"
              }
            />

            {account.generated_letter_id ? (
              <>
                <ActionButton
                  text="👁️ View Letter"
                  onClick={() =>
                    onViewLetter?.(
                      account.generated_letter_id
                    )
                  }
                  background="#16a34a"
                />

                <ActionButton
                  text="⬇️ Download PDF"
                  onClick={() =>
                    onDownloadPdf?.(
                      account.generated_letter_id
                    )
                  }
                  background="#D4AF37"
                />
              </>
            ) : null}

            <ActionButton
              text={
                protectAccount
                  ? "🛡️ Protected Account"
                  : "🛡️ Protect Account"
              }
              disabled
              background="#64748b"
            />

            <ActionButton
              text="⚖️ CFPB Complaint"
              disabled
              background="#64748b"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ title, value }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        borderRadius: "16px",
        padding: "18px",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: "13px",
          marginBottom: "8px",
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontWeight: "800",
          fontSize: "18px",
          color: "#0f172a",
        }}
      >
        {value || "--"}
      </div>
    </div>
  );
}

function Section({ title, content }) {
  return (
    <div style={{ marginTop: "26px" }}>
      <div
        style={{
          fontSize: "16px",
          fontWeight: "800",
          marginBottom: "8px",
        }}
      >
        {title}
      </div>

      <div
        style={{
          background: "#f8fafc",
          borderRadius: "14px",
          padding: "16px",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        {content || "No information available."}
      </div>
    </div>
  );
}

function ActionButton({
  text,
  onClick,
  disabled = false,
  background = "#D4AF37",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: "none",
        background: disabled
          ? "#94a3b8"
          : background,
        color: disabled
          ? "white"
          : "#111111",
        padding: "16px",
        borderRadius: "14px",
        cursor: disabled
          ? "not-allowed"
          : "pointer",
        fontWeight: "800",
        fontSize: "15px",
        opacity: disabled ? 0.75 : 1,
      }}
    >
      {text}
    </button>
  );
}
